/**
 * Reading the jumps of a bar: the segno, the coda sign, To Coda, Fine, D.C. and D.S.
 *
 * MusicXML states a jump in up to three ways at once, and files in the wild use any one of
 * them. `<sound dacapo="yes"/>` is what a sequencer reads and what a well-made file
 * carries; `<segno/>` and `<coda/>` are the two signs as glyphs; and a great many files
 * engraved by hand carry nothing but `<words>D.C. al Fine</words>`, which is what a player
 * reads. All three are accepted, and a bar that says the same thing twice says it once.
 *
 * The words are matched loosely on purpose -- `D.C.`, `D.C. al Fine`, `Da Capo al Coda`
 * and `DC al fine` are one instruction written four ways, and a reader that took only the
 * first would play the other three straight through. The cost of the looseness is that a
 * direction reading `Fine` is read as an ending rather than as a word over the staff, and
 * that is what it is.
 *
 * The two abbreviations are not matched so. They are read only where they are written as
 * abbreviations, a `D` and then a `C` or an `S` with nothing between them but a full stop:
 * words whose letters merely begin that way -- a harpist's pedals `D♮ C♮ B♭`, a `D string`
 * -- are words over the staff, and reading them as a jump would play the piece twice.
 */

import { attr, child, childrenNamed, textOf } from "../xml/read";
import type { XmlElement } from "../xml/parse";

/** What a bar says about jumping, gathered as the parts of it are read. */
export interface JumpRead {
  segno: boolean;
  coda: boolean;
  toCoda: boolean;
  fine: boolean;
  jump: "daCapo" | "dalSegno" | null;
}

/** A fresh bar states no jump. */
export function noJumps(): JumpRead {
  return { segno: false, coda: false, toCoda: false, fine: false, jump: null };
}

/** Letters only, lower case: what `D.S. al Coda` and `DS al coda` have in common. */
function plainly(words: string): string {
  return words.toLowerCase().replace(/[^a-z]/g, "");
}

/** `D.C.` or `D.S.` written as the abbreviation it is, alone or before more words. */
const ABBREVIATED = /^\W*d(?:\.\s*)?([cs])\b/i;

/** What a line of words instructs, when it instructs anything. */
function instruction(words: string): Partial<JumpRead> | null {
  const text = plainly(words);
  if (text === "") return null;
  const short = ABBREVIATED.exec(words)?.[1]?.toLowerCase();
  if (text.startsWith("dacapo") || short === "c") return { jump: "daCapo" };
  if (text.startsWith("dalsegno") || short === "s") return { jump: "dalSegno" };
  if (text.startsWith("tocoda") || text === "alcoda") return { toCoda: true };
  if (text === "fine") return { fine: true };
  return null;
}

/** What a `<sound>` element says about jumping, by the attributes only it carries. */
function sounded(sound: XmlElement): Partial<JumpRead> | null {
  const marks: Partial<JumpRead> = {};
  if (attr(sound, "segno") !== null) marks.segno = true;
  if (attr(sound, "coda") !== null) marks.coda = true;
  if (attr(sound, "tocoda") !== null) marks.toCoda = true;
  if (attr(sound, "fine") !== null) marks.fine = true;
  if (attr(sound, "dacapo") !== null) marks.jump = "daCapo";
  if (attr(sound, "dalsegno") !== null) marks.jump = "dalSegno";
  return Object.keys(marks).length === 0 ? null : marks;
}

/** Fold what was read into what the bar already says, without unsaying any of it. */
function keep(into: JumpRead, marks: Partial<JumpRead>): void {
  if (marks.segno === true) into.segno = true;
  if (marks.coda === true) into.coda = true;
  if (marks.toCoda === true) into.toCoda = true;
  if (marks.fine === true) into.fine = true;
  if (marks.jump !== undefined && marks.jump !== null) into.jump ??= marks.jump;
}

/**
 * Read one `<direction>` as a jump, answering whether it was one.
 *
 * `true` means the caller has nothing left to do with it: a direction that says `To Coda`
 * is that instruction and not also a word written over the staff, and storing it twice
 * would print it twice on the way back out.
 */
export function readJumpDirection(element: XmlElement, into: JumpRead): boolean {
  const said = noJumps();
  let found = false;

  for (const type of childrenNamed(element, "direction-type")) {
    if (child(type, "segno") !== null) {
      keep(said, { segno: true });
      found = true;
    }
    if (child(type, "coda") !== null) {
      keep(said, { coda: true });
      found = true;
    }
    const words = textOf(type, "words");
    const meant = words === null ? null : instruction(words);
    if (meant !== null) {
      keep(said, meant);
      found = true;
    }
  }

  const sound = child(element, "sound");
  const played = sound === null ? null : sounded(sound);
  if (played !== null) {
    keep(said, played);
    found = true;
  }

  // A coda glyph drawn at a `To Coda` is the sign being quoted so the player knows where
  // they are going, not the coda itself. The instruction in the same direction says which
  // of the two it is, which is why the direction is resolved whole rather than as it is read.
  if (said.toCoda) said.coda = false;
  keep(into, said);
  return found;
}

/** Read a `<sound>` standing on its own in a bar, which is where some files put a jump. */
export function readJumpSound(element: XmlElement, into: JumpRead): void {
  const played = sounded(element);
  if (played !== null) keep(into, played);
}
